import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, User, Heart, LogOut, ChevronDown } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useFavorites } from '../../context/FavoritesContext';
import { navLinks, products } from '../../data/mockData';
import CartDrawer from '../cart/CartDrawer';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const { getCartCount, setIsCartOpen } = useCart();
  const { user, isAuthenticated, login, logout } = useAuth();
  const { favorites } = useFavorites();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
    setIsUserMenuOpen(false);
  }, [location]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase();
      const results = products.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query)
      );
      setSearchResults(results.slice(0, 5));
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/urunler?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const isHomePage = location.pathname === '/';
  const isAboutPage = location.pathname === '/hakkimizda';
  const hasVideoHero = isHomePage || isAboutPage;
  const headerBg = isScrolled || !hasVideoHero ? 'bg-white shadow-sm' : 'bg-transparent';
  const textColor = isScrolled || !hasVideoHero ? 'text-black' : 'text-white';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}
      >
        {/* Top Bar */}
        <div className={`hidden lg:block border-b ${isScrolled || !isHomePage ? 'border-gray-100' : 'border-white/20'}`}>
          <div className="max-w-7xl mx-auto px-6 py-2">
            <p className={`text-center text-xs tracking-widest ${textColor} opacity-80`}>
              ÜCRETSİZ KARGO | 500₺ ÜZERİ SİPARİŞLERDE
            </p>
          </div>
        </div>

        {/* Main Header */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - Sol tarafta */}
            <Link to="/" className="flex-shrink-0">
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/6bz19s82_Valmera%20Logo%20%281%29.png"
                alt="VALMÉRA"
                className={`h-12 w-auto transition-all duration-300 ${isScrolled || !hasVideoHero ? '' : 'brightness-0 invert'}`}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="text-2xl font-light tracking-widest">VALMÉRA</span>';
                }}
              />
            </Link>

            {/* Desktop Navigation - Ortada */}
            <nav className="hidden lg:flex items-center space-x-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm tracking-wider font-light hover:opacity-70 transition-opacity ${textColor} ${
                    location.pathname === link.path ? 'border-b border-current' : ''
                  }`}
                >
                  {link.name.toUpperCase()}
                </Link>
              ))}
            </nav>

            {/* Right Icons - Sağ tarafta */}
            <div className="flex items-center space-x-4">
              {/* Search */}
              <button
                className={`${textColor} p-2 hover:opacity-70 transition-opacity`}
                onClick={() => {
                  setIsSearchOpen(!isSearchOpen);
                  setTimeout(() => searchInputRef.current?.focus(), 100);
                }}
              >
                <Search size={20} />
              </button>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  className={`hidden lg:flex items-center gap-1 ${textColor} p-2 hover:opacity-70 transition-opacity`}
                  onClick={() => {
                    if (isAuthenticated) {
                      setIsUserMenuOpen(!isUserMenuOpen);
                    } else {
                      navigate('/giris');
                    }
                  }}
                >
                  {isAuthenticated && user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <User size={20} />
                  )}
                  {isAuthenticated && <ChevronDown size={14} />}
                </button>

                {/* User Dropdown */}
                {isUserMenuOpen && isAuthenticated && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white shadow-lg py-2 z-50">
                    <div className="px-4 py-3 border-b">
                      <p className="text-sm font-medium truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    {(user?.email === 'admin@valmera.com' || user?.email === 'hafizecemileogut@gmail.com') && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 text-purple-600 font-medium"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>
                        Yönetici Paneli
                      </Link>
                    )}
                    <Link
                      to="/hesabim"
                      className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50"
                      data-testid="user-menu-account-link"
                    >
                      <User size={16} />
                      Hesabım
                    </Link>
                    <Link
                      to="/favoriler"
                      className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50"
                    >
                      <Heart size={16} />
                      Favorilerim
                      {favorites.length > 0 && (
                        <span className="ml-auto bg-black text-white text-xs px-2 py-0.5 rounded-full">
                          {favorites.length}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/siparislerim"
                      className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50"
                    >
                      <ShoppingBag size={16} />
                      Siparişlerim
                    </Link>
                    <button
                      onClick={logout}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut size={16} />
                      Çıkış Yap
                    </button>
                  </div>
                )}
              </div>

              {/* Favorites */}
              <Link
                to={isAuthenticated ? '/favoriler' : '/giris'}
                className={`hidden lg:block ${textColor} p-2 hover:opacity-70 transition-opacity relative`}
              >
                <Heart size={20} />
                {isAuthenticated && favorites.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {favorites.length}
                  </span>
                )}
              </Link>

              {/* Cart */}
              <button
                className={`${textColor} p-2 hover:opacity-70 transition-opacity relative`}
                onClick={() => setIsCartOpen(true)}
              >
                <ShoppingBag size={20} />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
              </button>

              {/* Mobile Menu Button - Sağda */}
              <button
                data-testid="mobile-menu-btn"
                className={`lg:hidden ${textColor} p-2`}
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Menüyü aç"
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div
          className={`overflow-hidden transition-all duration-300 bg-white ${
            isSearchOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 py-4">
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Ürün ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-50 border-0 py-3 px-4 pr-12 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              />
              <button type="submit" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black">
                <Search size={18} />
              </button>
            </form>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <p className="text-xs text-gray-500 mb-3">Sonuçlar</p>
                <div className="space-y-2">
                  {searchResults.map((product) => (
                    <Link
                      key={product.id}
                      to={`/urun/${product.id}`}
                      className="flex items-center gap-4 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => {
                        setIsSearchOpen(false);
                        setSearchQuery('');
                      }}
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/48x48?text=V';
                        }}
                      />
                      <div>
                        <p className="text-sm font-medium">{product.name}</p>
                        <p className="text-xs text-gray-500">
                          {new Intl.NumberFormat('tr-TR', {
                            style: 'currency',
                            currency: 'TRY',
                            minimumFractionDigits: 0,
                          }).format(product.prices?.['65x65'] || product.price)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to={`/urunler?search=${encodeURIComponent(searchQuery)}`}
                  className="block text-center text-sm text-black underline mt-4"
                  onClick={() => {
                    setIsSearchOpen(false);
                    setSearchQuery('');
                  }}
                >
                  Tüm sonuçları gör
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu - Sağdan Açılan */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 bottom-0 w-80 bg-white transform transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/6bz19s82_Valmera%20Logo%20%281%29.png"
                alt="VALMÉRA"
                className="h-10"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.outerHTML = '<span class="text-xl font-light tracking-widest">VALMÉRA</span>';
                }}
              />
              <button onClick={() => setIsMobileMenuOpen(false)}>
                <X size={24} />
              </button>
            </div>

            {/* User Section - Mobile */}
            <div className="mb-8 pb-6 border-b">
              {isAuthenticated && user ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User size={20} />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Link
                      to="/hesabim"
                      className="flex items-center gap-2 text-sm hover:text-gray-600"
                      data-testid="mobile-menu-account-link"
                    >
                      <User size={16} />
                      Hesabım
                    </Link>
                    <Link
                      to="/favoriler"
                      className="flex items-center gap-2 text-sm hover:text-gray-600"
                    >
                      <Heart size={16} />
                      Favorilerim ({favorites.length})
                    </Link>
                    <Link
                      to="/siparislerim"
                      className="flex items-center gap-2 text-sm hover:text-gray-600"
                    >
                      <ShoppingBag size={16} />
                      Siparişlerim
                    </Link>
                    <button
                      onClick={logout}
                      className="flex items-center gap-2 text-sm text-red-600"
                    >
                      <LogOut size={16} />
                      Çıkış Yap
                    </button>
                  </div>
                </div>
              ) : (
                <Link
                  to="/giris"
                  className="flex items-center gap-2 w-full bg-black text-white py-3 justify-center text-sm"
                >
                  <User size={18} />
                  Giriş Yap / Üye Ol
                </Link>
              )}
            </div>

            <nav className="space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-lg tracking-wider font-light hover:opacity-70 transition-opacity"
                >
                  {link.name.toUpperCase()}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <CartDrawer />
    </>
  );
};

export default Header;
